// Frontend/src/pages/ActivityLogs.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Avatar,
  CircularProgress,
  Alert
} from '@mui/material';
import { logAPI, invoiceAPI } from '../api/api';

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    resourceType: '',
    action: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [resourceDetails, setResourceDetails] = useState({}); // Cache for resource details

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs, filters, searchQuery, resourceDetails]);

  // Fetch details for invoice resources
  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      // Get logs that are for invoices with no details in cache yet
      const invoiceLogs = logs.filter(log => 
        log.resource_type === 'invoice' && 
        !resourceDetails[`invoice_${log.resource_id}`]
      );
      
      for (const log of invoiceLogs) {
        try {
          const response = await invoiceAPI.getInvoice(log.resource_id);
          const invoice = response.data;
          setResourceDetails(prev => ({
            ...prev,
            [`invoice_${log.resource_id}`]: invoice.invoice_number || `#${log.resource_id}`
          }));
        } catch (err) {
          // If invoice is not found (maybe deleted), just use the ID
          setResourceDetails(prev => ({
            ...prev,
            [`invoice_${log.resource_id}`]: `#${log.resource_id}`
          }));
        }
      }
    };

    if (logs.length > 0) {
      fetchInvoiceDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await logAPI.getActivityLogs();
      setLogs(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch activity logs. Please try again later.');
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = logs;

    // Apply resource type filter
    if (filters.resourceType) {
      result = result.filter(log => log.resource_type === filters.resourceType);
    }

    // Apply action filter
    if (filters.action) {
      result = result.filter(log => log.action === filters.action);
    }

    // Apply search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(log =>
        log.display_name.toLowerCase().includes(query) ||
        log.username.toLowerCase().includes(query) ||
        log.description.toLowerCase().includes(query) ||
        log.resource_type.toLowerCase().includes(query)
      );
    }

    setFilteredLogs(result);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'created':
        return 'success';
      case 'updated':
        return 'warning';
      case 'deleted':
        return 'error';
      case 'uploaded':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getResourceIcon = (resourceType) => {
    switch (resourceType) {
      case 'invoice':
        return '📄';
      case 'vendor':
        return '🏢';
      case 'category':
        return '🏷️';
      case 'user':
        return '👤';
      default:
        return '⚙️';
    }
  };

  const getResourceName = (resourceType, resourceId) => {
    return `${resourceType.charAt(0).toUpperCase() + resourceType.slice(1)}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
        Activity Logs
      </Typography>

      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search logs"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Resource Type</InputLabel>
                <Select
                  name="resourceType"
                  value={filters.resourceType}
                  onChange={handleFilterChange}
                  label="Resource Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="invoice">Invoice</MenuItem>
                  <MenuItem value="vendor">Vendor</MenuItem>
                  <MenuItem value="category">Category</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  name="action"
                  value={filters.action}
                  onChange={handleFilterChange}
                  label="Action"
                >
                  <MenuItem value="">All Actions</MenuItem>
                  <MenuItem value="created">Created</MenuItem>
                  <MenuItem value="updated">Updated</MenuItem>
                  <MenuItem value="deleted">Deleted</MenuItem>
                  <MenuItem value="uploaded">Uploaded</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
            <Table sx={{ minWidth: 650 }} aria-label="activity logs table">
              <TableHead sx={{ backgroundColor: '#f5f7fa' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Resource</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLogs.length > 0 ? (
                  filteredLogs.map((log) => (
                    <TableRow
                      key={log.id}
                      sx={{ 
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ width: 32, height: 32, mr: 1, fontSize: '0.8rem', bgcolor: 'primary.main' }}>
                            {log.display_name ? log.display_name.charAt(0).toUpperCase() : log.username?.charAt(0).toUpperCase() || 'U'}
                          </Avatar>
                          <Typography variant="body2">{log.display_name || log.username}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.action}
                          color={getActionColor(log.action)}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: '8px' }}>{getResourceIcon(log.resource_type)}</span>
                          <Typography variant="body2">{getResourceName(log.resource_type, log.resource_id)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.description || 'No description provided'}
                        </Typography>
                        {log.details && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            {log.details}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(log.created_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No activity logs found matching your criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ActivityLogs;